import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  StatCard,
  StatusBadge,
  Avatar,
  GhostBtn,
  ListRow,
} from "components/dashboard/ui";
import {
  CheckCircle2,
  AlertOctagon,
  ClipboardList,
  Wallet,
  ShoppingCart,
  PenTool,
  Store,
  Briefcase,
  IndianRupee,
  Receipt,
  Boxes,
  Check,
  Building2,
} from "lucide-react";
import {
  IndividualDashboardApi,
  SubscriptionAPI,
  UserAPI,
} from "api/dashboard/service";
import { formatCurrencyShort } from "utils/utilityFunctions";
import ReminderActionDrawer, {
  getCompletedReminderActionLabel,
  getReminderActionLabel,
} from "./ReminderActionDrawer";
import { useNavigate } from "react-router-dom";
import { IoLocationOutline } from "react-icons/io5";
import { GoClock } from "react-icons/go";

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const ACTION_TYPE_META = {
  INVENTORY_UPDATE: { icon: Boxes },
  TASK_UPDATE: { icon: ClipboardList },
  SUBTASK_UPDATE: { icon: ClipboardList },
  WORKER_ATTENDANCE: { icon: Briefcase },
  CONTRACTOR_ATTENDANCE: { icon: Briefcase },
  VENDOR_PAYMENT: { icon: IndianRupee },
  CONTRACTOR_PAYMENT: { icon: IndianRupee },
  SITE_UPDATE: { icon: Receipt },
  WORKER_PAYMENT: { icon: Wallet },
};

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clampPercent = (value) =>
  Math.min(100, Math.max(0, Math.round(safeNumber(value))));

const getReminderProgress = (reminder) => {
  const progress = reminder?.progress || EMPTY_OBJECT;
  const total = safeNumber(progress?.total);
  const completed = Math.min(
    safeNumber(progress?.completed),
    total || Number.MAX_SAFE_INTEGER,
  );
  const percent = total ? clampPercent((completed / total) * 100) : 0;
  const label =
    progress?.label ||
    (total
      ? `${completed}/${total} completed`
      : reminder?.is_completed
        ? "Completed"
        : "");

  return {
    completed,
    total,
    percent,
    label,
    isComplete: Boolean(progress?.is_complete ?? reminder?.is_completed),
  };
};

const getStoredOrganizationId = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("organization") || "null")?.id || null
    );
  } catch (_) {
    return null;
  }
};

const hasActiveSubscription = (response) =>
  Boolean(
    response?.status &&
    response?.data?.active_flag &&
    response?.data?.valid_till > new Date().toISOString(),
  );

const unwrapData = (response) => response?.data ?? response ?? null;

const getCurrentMember = (response, user) => {
  const data = unwrapData(response);
  const members = Array.isArray(data) ? data : data ? [data] : [];
  if (!members.length) return null;
  return members.find((member) => member?.user?.id === user?.id) || members[0];
};

const initials = (name, fallback = "?") =>
  String(name || "")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || fallback;

const timeAgo = (dateLike) => {
  if (!dateLike) return "Just now";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return String(dateLike);
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const formatTime = (timeLike) => {
  if (!timeLike) return "Today";
  const [hour = "0", minute = "0"] = String(timeLike).split(":");
  const date = new Date();
  date.setHours(Number(hour), Number(minute), 0, 0);
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getLineSummary = (lines = []) => {
  const names = lines.map((line) => line?.material_name).filter(Boolean);
  if (!names.length) return "Material request";
  if (names.length === 1) return names[0];
  return `${names.slice(0, 2).join(", ")}${names.length > 2 ? ` +${names.length - 2}` : ""}`;
};

const getRecordLabel = (recordType) => {
  const type = String(recordType || "").toLowerCase();
  if (type.includes("purchase")) return "PO";
  if (type.includes("rfq")) return "RFQ";
  if (type.includes("material")) return "MR";
  return "REQ";
};

const statusTone = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (
    normalized.includes("complete") ||
    normalized.includes("approved") ||
    normalized.includes("executed") ||
    normalized.includes("solved")
  )
    return "good";
  if (
    normalized.includes("pending") ||
    normalized.includes("due") ||
    normalized.includes("open") ||
    normalized.includes("active")
  )
    return "warn";
  if (
    normalized.includes("reject") ||
    normalized.includes("overdue") ||
    normalized.includes("cancel")
  )
    return "alert";
  return "neutral";
};

const normalizeAccess = (member, summaryMember) => {
  const source = member || EMPTY_OBJECT;
  const summaryAccess = summaryMember?.access || EMPTY_OBJECT;
  const isAdmin = Boolean(source?.is_admin || summaryMember?.is_admin);
  const has = (...keys) =>
    isAdmin ||
    keys.some((key) => Boolean(source?.[key] || summaryAccess?.[key]));

  return {
    finance: has("finance_control"),
    procurement: has("procurement_control"),
    vendor: has("vendor_control"),
    contractor: has("contractor_control"),
    design: has("design_management", "design_admin", "design_control"),
    snags: has("snag_admin"),
    isAdmin,
  };
};

const mapReminder = (item) => {
  const Icon = ACTION_TYPE_META[item?.action_type]?.icon || ClipboardList;
  const dueState = String(item?.due_state || "").toUpperCase();
  const progress = getReminderProgress(item);
  return {
    raw: item,
    id: item?.id,
    icon: Icon,
    title: item?.title || "Scheduled action",
    project: item?.site_name || item?.scope || "Global",
    site: item?.site,
    siteName: item?.site_name,
    actionType: item?.action_type,
    actionLabel: getReminderActionLabel(item?.action_type),
    completedActionLabel: getCompletedReminderActionLabel(item?.action_type),
    time: formatTime(item?.due_time),
    urgency:
      dueState === "OVERDUE"
        ? "overdue"
        : dueState === "DUE"
          ? "high"
          : undefined,
    completed: progress.isComplete,
    progress,
  };
};

const getScheduleSortRank = (item) => {
  if (item?.completed) return 4;
  if (item?.urgency === "overdue") return 0;
  if (item?.urgency === "high") return 1;
  return 2;
};

const getTimeSortValue = (item) => {
  const rawTime = item?.raw?.due_time;
  if (!rawTime) return Number.MAX_SAFE_INTEGER;
  const [hours = "0", minutes = "0"] = String(rawTime).split(":");
  return safeNumber(hours) * 60 + safeNumber(minutes);
};

function IndividualDashboard({ hasNoOrganization = false }) {
  const [organizationId, setOrganizationId] = useState(getStoredOrganizationId);
  const [currentMember, setCurrentMember] = useState(null);
  const [summary, setSummary] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const [, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeReminderAction, setActiveReminderAction] = useState(null);
  const navigate = useNavigate();

  const handleDesignPinClick = (designId) => {
    if (!designId) return;
    navigate(`/dashboard/design?designId=${encodeURIComponent(designId)}`);
  };

  const handleVendorClick = (vendorId) => {
    if (!vendorId) return;
    navigate(`/dashboard/vendor?vendorId=${encodeURIComponent(vendorId)}`);
  };

  useEffect(() => {
    const syncOrganization = () => setOrganizationId(getStoredOrganizationId());
    window.addEventListener("storage", syncOrganization);
    window.addEventListener("organizationChanged", syncOrganization);
    return () => {
      window.removeEventListener("storage", syncOrganization);
      window.removeEventListener("organizationChanged", syncOrganization);
    };
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setSubscriptionActive(null);
      return;
    }

    let cancelled = false;

    SubscriptionAPI.checkUserSubscription(organizationId)
      .then((response) => {
        if (!cancelled) setSubscriptionActive(hasActiveSubscription(response));
      })
      .catch((subscriptionError) => {
        if (cancelled) return;
        console.error(
          "Error checking individual dashboard subscription:",
          subscriptionError,
        );
        setSubscriptionActive(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const loadDashboard = useCallback(() => {
    if (!organizationId) {
      setError("");
      setSummary(null);
      setReminders([]);
      return () => {};
    }

    let cancelled = false;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const userResponse = await UserAPI.getUserDetails();
        if (cancelled) return;
        const userData = unwrapData(userResponse);

        const memberResponse =
          await IndividualDashboardApi.getCurrentMember(organizationId);
        if (cancelled) return;
        setCurrentMember(getCurrentMember(memberResponse, userData));

        const remindersPromise = IndividualDashboardApi.refreshTodayReminders(
          organizationId,
        ).then(() => IndividualDashboardApi.getTodayReminders(organizationId));

        const [summaryResult, remindersResult] = await Promise.allSettled([
          IndividualDashboardApi.getSummary(organizationId),
          remindersPromise,
        ]);

        if (cancelled) return;
        if (summaryResult.status === "fulfilled") {
          setSummary(unwrapData(summaryResult.value));
        } else {
          setSummary(null);
        }

        if (remindersResult.status === "fulfilled") {
          const reminderData = unwrapData(remindersResult.value);
          setReminders(Array.isArray(reminderData) ? reminderData : []);
        } else {
          setReminders([]);
        }

        const failures = [summaryResult, remindersResult].filter(
          (result) => result.status === "rejected",
        );
        if (failures.length) {
          console.error(
            "Error fetching individual dashboard:",
            failures.map((failure) => failure.reason),
          );
          setError("Some dashboard data could not be loaded.");
        }
      } catch (fetchError) {
        if (cancelled) return;
        console.error("Error fetching individual dashboard:", fetchError);
        setError(fetchError?.message || "Could not load dashboard.");
        setSummary(null);
        setReminders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => loadDashboard(), [loadDashboard]);

  const taskOverview = summary?.task_overview || EMPTY_OBJECT;
  const pendingApprovals = summary?.pending_approvals_overview || EMPTY_OBJECT;
  const snagsOverview = summary?.snags_overview || EMPTY_OBJECT;
  const procurementOverview = summary?.procurement_overview || EMPTY_OBJECT;
  const financeOverview = summary?.finance_overview || EMPTY_OBJECT;
  const designOverview = summary?.design_overview || EMPTY_OBJECT;
  const contractorsOverview = summary?.contractors_overview || EMPTY_OBJECT;
  const vendorOverview = summary?.vendors_overview || EMPTY_ARRAY;
  const lowStockAlerts = summary?.low_stock_inventory_alerts || EMPTY_ARRAY;

  const access = useMemo(
    () => normalizeAccess(currentMember, summary?.member),
    [currentMember, summary?.member],
  );

  const totalTasks = safeNumber(taskOverview?.total_tasks);
  const completedTasks = safeNumber(taskOverview?.completed_tasks);
  const productivity = totalTasks
    ? clampPercent((completedTasks / totalTasks) * 100)
    : 0;
  const overdueTasks = safeNumber(taskOverview?.overdue_tasks);
  const delayedTasks = safeNumber(taskOverview?.delayed_tasks);
  const remindersDone = reminders.filter(
    (item) => getReminderProgress(item).isComplete,
  ).length;
  const pendingReminderCount = reminders.length - remindersDone;

  const scheduleItems = useMemo(
    () =>
      reminders.map(mapReminder).sort((left, right) => {
        const rankDelta =
          getScheduleSortRank(left) - getScheduleSortRank(right);
        if (rankDelta !== 0) return rankDelta;
        return getTimeSortValue(left) - getTimeSortValue(right);
      }),
    [reminders],
  );

  const actionFlowReminders = useMemo(
    () =>
      scheduleItems
        .filter((item) => item.actionLabel && !item.completed)
        .map((item) => item.raw),
    [scheduleItems],
  );

  const recentProcurement = useMemo(
    () =>
      (procurementOverview?.recent_requests || []).slice(0, 5).map((item) => {
        const total = (item?.lines || []).reduce(
          (sum, line) => sum + safeNumber(line?.total_price),
          0,
        );
        return {
          type: getRecordLabel(item?.record_type),
          item: getLineSummary(item?.lines),
          meta: `${item?.rfq_number || item?.po_number || item?.mr_number || `#${item?.id}`} · ${item?.site_name || "No site"} · ${timeAgo(item?.created_on || item?.order_date)}`,
          amount: total
            ? formatCurrencyShort(total)
            : `${(item?.lines || []).length} items`,
        };
      }),
    [procurementOverview?.recent_requests],
  );

  const materialInventory = useMemo(
    () =>
      lowStockAlerts.slice(0, 5).map((item) => {
        const stock = safeNumber(item?.current_stock);
        const value = stock <= 0 ? 8 : clampPercent(stock);
        return {
          material: item?.material_name || "Material",
          site: item?.site_name || "Site",
          qty: `${stock.toLocaleString("en-IN")} ${item?.uom || ""}`.trim(),
          value,
          tone:
            stock <= 0
              ? "bg-red-500"
              : stock <= 10
                ? "bg-amber-500"
                : "bg-emerald-500",
          text:
            stock <= 0
              ? "text-red-600"
              : stock <= 10
                ? "text-amber-600"
                : "text-emerald-600",
        };
      }),
    [lowStockAlerts],
  );

  const recentSnags = useMemo(
    () =>
      (snagsOverview?.recent || []).slice(0, 3).map((snag) => {
        const priority = safeNumber(snag?.priority);
        return {
          title: snag?.title || "Snag",
          site: snag?.site_name || "No site",
          age: timeAgo(snag?.last_updated_on || snag?.created_on),
          priority: priority >= 3 ? "alert" : priority >= 2 ? "warn" : "info",
          label:
            snag?.status ||
            (priority >= 3 ? "Critical" : priority >= 2 ? "High" : "Watch"),
        };
      }),
    [snagsOverview?.recent],
  );

  const recentPins = useMemo(
    () =>
      (designOverview?.recent_pins || []).slice(0, 3).map((pin) => ({
        title: pin?.comments || pin?.design_name || "Design pin",
        design_id: pin?.design_id,
        sub: `${pin?.created_by || "Team"} · ${timeAgo(pin?.created_on)}`,
        initials: initials(pin?.created_by, "DS"),
      })),
    [designOverview?.recent_pins],
  );

  const vendors = useMemo(
    () =>
      vendorOverview.slice(0, 3).map((vendor) => {
        const purchaseValue = safeNumber(vendor?.total_purchase_value);
        const paid = safeNumber(vendor?.total_paid);
        const due = Math.max(0, purchaseValue - paid);
        return {
          name: vendor?.vendor_name || "Vendor",
          vendor_id: vendor?.vendor_id,
          spend: formatCurrencyShort(purchaseValue),
          due: due ? formatCurrencyShort(due) : "Settled",
          tone: due ? "warn" : "good",
          urgent: Boolean(due),
        };
      }),
    [vendorOverview],
  );

  const workOrders = useMemo(
    () =>
      (contractorsOverview?.recent_active_workorders || [])
        .slice(0, 3)
        .map((order) => ({
          id: `WO-${order?.work_order_id || order?.id}`,
          title: order?.contractor_name || "Work order",
          vendor: order?.contractor_name || "Contractor",
          site: order?.site_name || "No site",
          status: order?.status || "Active",
          tone: statusTone(order?.status),
          value: formatCurrencyShort(safeNumber(order?.total_value)),
        })),
    [contractorsOverview?.recent_active_workorders],
  );

  const pettyCashItems = useMemo(
    () =>
      (pendingApprovals?.recent_finance || []).slice(0, 3).map((item) => ({
        who: item?.site_name || item?.type || "Petty Cash",
        what:
          item?.category || item?.remarks || item?.type || "Petty cash request",
        amt: formatCurrencyShort(safeNumber(item?.amount)),
        status: item?.status || "PENDING",
      })),
    [pendingApprovals?.recent_finance],
  );

  const budget = safeNumber(financeOverview?.total_budget);
  const spent = safeNumber(financeOverview?.total_expenses);
  const burnPercent = budget ? clampPercent((spent / budget) * 100) : 0;
  const marginPercent = budget
    ? Math.round(((budget - spent) / budget) * 100)
    : 0;

  if (hasNoOrganization || !organizationId) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-6 py-10">
        <div className="w-full max-w-md rounded-lg border border-border bg-sm-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full bg-blue-100 text-blue-700">
            <Building2 className="size-7" />
          </div>
          <h2 className="mb-3 text-2xl font-semibold text-foreground">
            No associated organization
          </h2>
          <p className="text-sm leading-6 text-gray-600">
            You have no associated organization. Please ask your organization
            owner/admin to add you to their organization.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-10 pt-8 pb-4">
      {/* <PageHeader title={`Good morning, ${userName} 👋`} eyebrow="Global Dashboard">
                {loading && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                        Loading dashboard
                    </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />Productivity {productivity}%
                </span>
                <button className="px-4 py-2 rounded-xl text-sm font-semibold gradient-primary text-primary-foreground shadow-sm hover:shadow-elevated transition-shadow">+ Quick Log</button>
            </PageHeader> */}

      {organizationId && subscriptionActive === false && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertOctagon className="mt-0.5 size-5 shrink-0 text-amber-600" />
          <div>
            <div className="font-semibold">
              Your organization has no active subscription.
            </div>
            <div className="mt-0.5 text-amber-700">
              Please ask your organization admin to renew the subscription to
              access SiteMaster.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Productivity"
          value={<span className="text-emerald-600">{productivity}%</span>}
          sub={`${completedTasks}/${totalTasks} tasks done`}
          tone="good"
        >
          <div className="flex items-end gap-1 mt-3 h-6">
            {[20, 38, 55, 72, productivity || 8].map((v, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-emerald-500/70"
                style={{ height: `${Math.max(8, v)}%` }}
              />
            ))}
          </div>
        </StatCard>
        <StatCard
          label="Tasks Due Today"
          value={pendingReminderCount}
          sub={`${remindersDone}/${reminders.length} schedule items done`}
          tone="warn"
        />
        <StatCard
          label="Pending Approvals"
          value={safeNumber(pendingApprovals?.total)}
          sub="Petty cash, procurement & snags"
          tone="neutral"
        />
        <StatCard
          label="Delayed Actions"
          value={delayedTasks + overdueTasks}
          sub={`${overdueTasks} overdue tasks`}
          tone="alert"
        />
      </div>

      <div className="grid grid-cols-12 gap-6">
        <Card
          className="col-span-12 xl:col-span-7"
          title="Today's Schedule"
          icon={<CheckCircle2 className="size-3.5" />}
          action={
            <div className="flex items-center gap-2">
              <StatusBadge tone="info">
                {remindersDone}/{reminders.length} done
              </StatusBadge>
              {/* <button onClick={loadDashboard} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                                <Bot className="size-3.5" />AI auto-fill
                            </button> */}
            </div>
          }
        >
          <ul className="space-y-2">
            {scheduleItems.length ? (
              scheduleItems.map((t) => {
                const tone =
                  t.urgency === "overdue"
                    ? "alert"
                    : t.urgency === "high"
                      ? "warn"
                      : undefined;
                return (
                  <li
                    key={t.id}
                    onClick={() => {
                      if (t.actionLabel) setActiveReminderAction(t.raw);
                    }}
                    onKeyDown={(event) => {
                      if (!t.actionLabel || !["Enter", " "].includes(event.key))
                        return;
                      event.preventDefault();
                      setActiveReminderAction(t.raw);
                    }}
                    role={t.actionLabel ? "button" : undefined}
                    tabIndex={t.actionLabel ? 0 : undefined}
                    className={`flex items-center gap-3 p-3 rounded-lg border border-border hover:border-border-strong transition-colors ${t.actionLabel ? "cursor-pointer" : ""} ${tone === "alert" ? "border-l-4 border-l-red-500 bg-red-50/40" : tone === "warn" ? "border-l-4 border-l-amber-400 bg-amber-50/40" : ""}`}
                  >
                    <div
                      className={`size-5 rounded-md border-2 shrink-0 ${t.completed ? "border-emerald-500 bg-emerald-500" : "border-border-strong hover:border-primary"}`}
                    >
                      {t.completed && <Check className="size-4 text-white" />}
                    </div>
                    <div className="size-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <t.icon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-tight">
                        {t.title}
                      </div>
                      <div className="flex flex-row items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <IoLocationOutline size={12} />
                        <span>{t.project}</span>
                        <GoClock className="ml-2"/>
                        <span>{t.time}</span>
                      </div>
                      {t.progress?.label && (
                        <div className="mt-1 w-[85%] flex items-center gap-2">
                          <div className="h-1.5 flex-1 w-[70%] overflow-hidden rounded-full bg-muted">
                            <div
                              className={`h-full rounded-full ${t.completed ? "bg-emerald-500" : "bg-[#3B0069]"}`}
                              style={{ width: `${t.progress.percent}%` }}
                            />
                          </div>
                          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-muted-foreground">
                            {t.progress.label}
                          </span>
                        </div>
                      )}
                    </div>
                    {t.completed && <StatusBadge tone="good">DONE</StatusBadge>}
                    {!t.completed && t.urgency === "high" && (
                      <StatusBadge tone="alert">HIGH</StatusBadge>
                    )}
                    {!t.completed && t.urgency === "overdue" && (
                      <StatusBadge tone="warn">OVERDUE</StatusBadge>
                    )}
                    {t.actionLabel && !t.completed && (
                      <span className="shrink-0 rounded-lg bg-[#3B0069] px-3 py-1.5 text-xs font-semibold capitalize text-white transition hover:bg-[#2d0050]">
                        {t.actionLabel}
                      </span>
                    )}
                    {t.completed && t.completedActionLabel && (
                      <span className="shrink-0 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold capitalize text-white">
                        {t.completedActionLabel}
                      </span>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="p-4 text-center text-sm text-muted-foreground">
                No schedule items for today.
              </li>
            )}
            {/* <button onClick={loadDashboard} className="w-full py-2.5 rounded-lg border border-dashed border-primary/40 bg-sm-primary/5 text-primary text-sm font-semibold flex items-center justify-center gap-2 hover:bg-sm-primary/10 transition-colors">
                            <Sparkles className="size-4" />Generate with AI
                        </button> */}
          </ul>
        </Card>

        {access.finance && (
          <Card
            className="col-span-12 xl:col-span-5"
            title="Finance Overview"
            icon={<Wallet className="size-3.5" />}
            action={
              <button
                className="inline-flex items-center gap-1 text-xs text-sm-primary font-semibold cursor-pointer hover:underline"
                onClick={() => navigate("/dashboard/finance")}
              >
                My access →
              </button>
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">
                  Budget
                </div>
                <div className="mt-2 text-2xl font-bold tracking-tight font-mono">
                  {formatCurrencyShort(budget)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  across active projects
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">
                  Spent
                </div>
                <div className="mt-2 text-2xl font-bold tracking-tight font-mono">
                  {formatCurrencyShort(spent)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {burnPercent}% utilized
                </div>
              </div>
              <div>
                <div
                  className={`mt-0 text-xs uppercase tracking-wide text-muted-foreground font-bold`}
                >
                  Margin
                </div>
                <div
                  className={`mt-2 text-2xl font-bold tracking-tight font-mono ${marginPercent >= 0 ? "text-emerald-600" : "text-red-600"}`}
                >
                  {marginPercent >= 0 ? "+" : ""}
                  {marginPercent}%
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  vs plan
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="text-xs uppercase tracking-wide text-muted-foreground font-bold">
                  Burn vs Plan
                </span>
                <StatusBadge
                  tone={
                    burnPercent > 100
                      ? "alert"
                      : burnPercent > 75
                        ? "warn"
                        : "good"
                  }
                >
                  {burnPercent}% used
                </StatusBadge>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-sm-primary"
                  style={{ width: `${burnPercent}%` }}
                />
              </div>
              <div className="mt-1.5 flex justify-between text-[10px] text-muted-foreground font-mono">
                <span>{formatCurrencyShort(spent)}</span>
                <span>{formatCurrencyShort(budget)}</span>
              </div>
            </div>
          </Card>
        )}

        <Card
          className="col-span-12 md:col-span-6 xl:col-span-5"
          title="Snags"
          icon={<AlertOctagon className="size-3.5" />}
          action={
            <StatusBadge tone="alert">
              {safeNumber(snagsOverview?.total_assigned_open)} Open
            </StatusBadge>
          }
        >
          <div className="space-y-2">
            {recentSnags.length ? (
              recentSnags.map((s, i) => (
                <div
                  key={`${s.title}-${i}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border border-border ${s.priority === "alert" ? "border-l-4 border-l-red-500" : "border-l-4 border-l-amber-400"}`}
                >
                  <div className="size-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                    <AlertOctagon className="size-4 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {s.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      📍 {s.site} · {s.age}
                    </div>
                  </div>
                  <StatusBadge tone={s.priority}>{s.label}</StatusBadge>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No recent snags.
              </div>
            )}
          </div>
        </Card>

        {access.procurement && (
          <Card
            className="col-span-12 md:col-span-6 xl:col-span-7"
            title="My Procurement"
            icon={<ShoppingCart className="size-3.5" />}
          >
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                {safeNumber(procurementOverview?.mrs_pending)} MR
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                {safeNumber(procurementOverview?.rfqs_open)} RFQ
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                {safeNumber(procurementOverview?.purchase_orders_open)} Open PO
              </span>
            </div>
            <div className="divide-y divide-border/60">
              {recentProcurement.length ? (
                recentProcurement.map((item) => (
                  <div
                    key={`${item.type}-${item.meta}`}
                    className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                  >
                    <span className="w-10 shrink-0 rounded-full bg-muted px-2 py-1 text-center text-[10px] font-bold text-muted-foreground">
                      {item.type}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">
                        {item.item}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {item.meta}
                      </div>
                    </div>
                    <div className="shrink-0 text-sm font-bold font-mono">
                      {item.amount}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No recent procurement.
                </div>
              )}
            </div>
          </Card>
        )}

        {access.design && (
          <Card
            className="col-span-12 md:col-span-6"
            title="Design Activity"
            icon={<PenTool className="size-3.5" />}
            action={
              <StatusBadge tone="info">
                {safeNumber(designOverview?.pending_pins)} Pending
              </StatusBadge>
            }
          >
            <div className="space-y-1">
              {recentPins.length ? (
                recentPins.map((d, i) => (
                  <ListRow
                    key={`${d.design_id || d.title}-${i}`}
                    leading={<Avatar initials={d.initials} tone="accent" />}
                    title={d.title}
                    sub={d.sub}
                    action={<GhostBtn>Review →</GhostBtn>}
                    onClick={
                      d.design_id
                        ? () => handleDesignPinClick(d.design_id)
                        : undefined
                    }
                  />
                ))
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No recent design activity.
                </div>
              )}
            </div>
          </Card>
        )}

        {access.vendor && (
          <Card
            className="col-span-12 md:col-span-6"
            title="Vendors"
            icon={<Store className="size-3.5" />}
          >
            <div className="space-y-1">
              {vendors.length ? (
                vendors.map((v, i) => (
                  <ListRow
                    key={`${v.vendor_id || v.name}-${i}`}
                    leading={
                      <div className="relative">
                        <Avatar
                          initials={initials(v.name)}
                          tone={v.tone === "good" ? "good" : "primary"}
                        />
                        {v.urgent && (
                          <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-amber-400 ring-2 ring-card" />
                        )}
                      </div>
                    }
                    title={v.name}
                    sub={
                      <span>
                        spend <span className="font-mono">{v.spend}</span> ·{" "}
                        {v.due === "Settled" ? (
                          <span className="text-emerald-600">settled</span>
                        ) : (
                          <span className="text-amber-600">
                            due <span className="font-mono">{v.due}</span>
                          </span>
                        )}
                      </span>
                    }
                    badge={
                      <StatusBadge tone={v.tone}>
                        {v.due === "Settled" ? "Settled" : "Pay"}
                      </StatusBadge>
                    }
                    onClick={
                      v.vendor_id
                        ? () => handleVendorClick(v.vendor_id)
                        : undefined
                    }
                  />
                ))
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No vendor activity.
                </div>
              )}
            </div>
          </Card>
        )}

        {access.contractor && (
          <Card
            className="col-span-12 xl:col-span-7"
            title="Active Work Orders"
            icon={<Briefcase className="size-3.5" />}
            action={
              <button
                className="text-xs text-sm-primary font-semibold cursor-pointer hover:underline"
                onClick={() => navigate("/dashboard/contractor")}
              >
                View all →
              </button>
            }
          >
            <div className="space-y-1">
              {workOrders.length ? (
                workOrders.map((w) => (
                  <div
                    key={w.id}
                    className="flex items-center gap-3 py-3 px-3 -mx-3 border-b border-border/50 last:border-0"
                  >
                    <div className="size-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Briefcase className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {w.id}
                      </div>
                      <div className="text-sm font-medium leading-tight truncate">
                        {w.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {w.vendor} · {w.site}
                      </div>
                    </div>
                    <StatusBadge tone={w.tone}>{w.status}</StatusBadge>
                    <div className="font-mono font-bold text-sm w-16 text-right">
                      {w.value}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No active work orders.
                </div>
              )}
            </div>
          </Card>
        )}

        {access.finance && (
          <Card
            className="col-span-12 xl:col-span-5"
            title="Petty Cash"
            icon={<Receipt className="size-3.5" />}
            action={
              <StatusBadge tone="warn">
                {safeNumber(pendingApprovals?.finance)} Pending
              </StatusBadge>
            }
          >
            <div className="space-y-1">
              {pettyCashItems.length ? (
                pettyCashItems.map((p, i) => (
                  <ListRow
                    key={`${p.what}-${i}`}
                    leading={
                      <Avatar
                        initials={initials(p.who, "PC")}
                        tone={statusTone(p.status)}
                      />
                    }
                    title={p.what}
                    sub={p.who}
                    badge={
                      <span className="font-mono font-bold text-sm">
                        {p.amt}
                      </span>
                    }
                    action={
                      <StatusBadge tone={statusTone(p.status)}>
                        {p.status}
                      </StatusBadge>
                    }
                  />
                ))
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No recent petty cash items.
                </div>
              )}
            </div>
          </Card>
        )}

        {access.procurement && (
          <Card
            className="col-span-12"
            title="Material Inventory"
            icon={<Boxes className="size-3.5" />}
            action={<StatusBadge tone="alert">Lowest stock first</StatusBadge>}
          >
            <div className="space-y-4">
              {materialInventory.length ? (
                materialInventory.map((item) => (
                  <div key={`${item.material}-${item.site}`}>
                    <div className="mb-2 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">
                          {item.material}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {item.site}
                        </div>
                      </div>
                      <div
                        className={`shrink-0 text-right text-sm font-bold font-mono ${item.text}`}
                      >
                        {item.qty}
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${item.tone}`}
                        style={{ width: `${item.value}%` }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No low-stock alerts.
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
      <ReminderActionDrawer
        reminder={activeReminderAction}
        reminders={actionFlowReminders}
        organizationId={organizationId}
        onClose={() => setActiveReminderAction(null)}
        onSaved={(event) => {
          if (event?.done !== false) {
            setActiveReminderAction(null);
          } else if (event?.nextReminder) {
            setActiveReminderAction(event.nextReminder);
          }
          loadDashboard();
        }}
      />
    </div>
  );
}

export default IndividualDashboard;
